import React, { useState, useEffect, useReducer } from "react";
import {
  Container,
  Paper,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Modal,
  Fab,
} from "@mui/material";
import { Delete as DeleteIcon, Add as AddIcon } from "@mui/icons-material";
import { message, Spin, Typography } from "antd";
import { useAuthContext } from "../../context/AuthContext";
import { API } from "../../utils/constant";
import { getToken } from "../../utils/helpers";
import UserStrategyForm from "./UserStrategyForm";

const initialState = (strategy = {}) => {
  console.log("strategy", strategy);

  // Ensure override_data is always a valid JSON string
  const overrideDataString = JSON.stringify(strategy.override_data || {}, null, 2);

  return {
    indices: strategy.indices || "",
    allocation: strategy.allocation || "",
    strategy_status: strategy.strategy_status || "",
    override_data: overrideDataString,
    user_broker_account: strategy.user_broker_account?.documentId || "",
  };
};


const reducer = (state, action) => {
  return { ...state, [action.name]: action.value };
};

const indicesList = [
  { value: "nifty", label: "Nifty" },
  { value: "bank_nifty", label: "Nifty Bank" },
  { value: "sensex", label: "Sensex" },
  { value: "fin_nifty", label: "Fin Nifty" },
  { value: "midcpnifty", label: "Mid Cap Nifty" },
];

export const StrategyEditor = ({ strategy, onDelete, onUpdate }) => {
  const [editingEnabled, setEditingEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [brokerAccounts, setBrokerAccounts] = useState([]);
  const [formData, dispatch] = useReducer(reducer, strategy, initialState);
  const [jsonError, setJsonError] = useState("");
  console.log("strategy inside editor",strategy)
  // Fetch broker accounts on mount
  useEffect(() => {
    const fetchBrokerAccounts = async () => {
      try {
        const authToken = getToken();
        const response = await fetch(`${API}/app-user-broker-accounts`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        const data = await response.json();
        setBrokerAccounts(data.data || []);
      } catch (error) {
        console.error("Failed to fetch broker accounts:", error);
      }
    };

    fetchBrokerAccounts();
  }, []);

  const handleEditToggle = () => setEditingEnabled((prev) => !prev);

  const handleChange = (event) => {
    if (!editingEnabled) return;
    const { name, value } = event.target;

    if (name === "override_data") {
      try {
        JSON.parse(value); // Validate JSON
        setJsonError(""); // Clear error if valid
      } catch (error) {
        setJsonError("Invalid JSON format");
      }
    }

    dispatch({ name, value });
  };

  const handleDelete = async () => {
    setLoading(true);
    await onDelete(strategy.documentId);
    setLoading(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    try {
      // Validate JSON before parsing
      const parsedOverrideData = JSON.parse(formData.override_data || "{}");

      const payload = { data: { ...formData, override_data: parsedOverrideData } };
      await onUpdate(strategy.documentId, payload);
      setLoading(false);
    } catch (error) {
      setJsonError("Invalid JSON format in override_data");
      message.error("Invalid JSON format! Please correct it before submitting.");
      setLoading(false);
    }
  };


  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <FormControlLabel
        control={<Switch checked={editingEnabled} onChange={handleEditToggle} />}
        label="Edit"
      />

      {Object.entries(formData).map(([key, value]) => {

        if (key === "indices") {
          return (
            <FormControl key={key} fullWidth margin="normal">
              <InputLabel id="indices-label">Indices</InputLabel>
              <Select
                labelId="indices-label"
                name="indices"
                value={formData.indices}
                onChange={handleChange}
                disabled={!editingEnabled}
                disablePortal
              >
                {indicesList.map((index) => (
                  <MenuItem key={index.value} value={index.value}>
                    {index.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          );
        }

        if (key === "strategy_status") {
          return (
            <FormControl key={key} fullWidth margin="normal">
              <InputLabel id="strategy-status-label">Status</InputLabel>
              <Select
                labelId="strategy-status-label"
                name="strategy_status"
                value={formData.strategy_status}
                onChange={handleChange}
                disabled={!editingEnabled}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          );
        }
        

        if (key === "user_broker_account") {
          return (
            <FormControl fullWidth margin="normal">
              <InputLabel id="broker-account-label">{ }</InputLabel>
              <Select
                labelId="broker-account-label"
                name="user_broker_account"
                value={brokerAccounts.some(acc => acc.documentId === formData.user_broker_account)
                  ? formData.user_broker_account
                  : ""} // Ensure value is valid
                onChange={handleChange}
                disabled={!editingEnabled}
              >
                {brokerAccounts.map((account) => (
                  <MenuItem key={account.documentId} value={account.documentId}>
                    {account.broker}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

          );
        }

        return (
          <TextField
            key={key}
            name={key}
            label={key}
            value={value}
            onChange={handleChange}
            fullWidth
            margin="normal"
            multiline={key === "override_data"}
            rows={key === "override_data" ? 4 : 1}
            disabled={!editingEnabled}
          />
        );
      })}

      <Button variant="contained" color="primary" onClick={handleSubmit} disabled={!editingEnabled || loading}>
        {loading ? <CircularProgress size={24} /> : "Update"}
      </Button>
      <Button variant="contained" color="secondary" onClick={handleDelete} startIcon={<DeleteIcon />} disabled={loading}>
        Delete
      </Button>
    </Paper>
  );
};

const Strategies = () => {
  const { user } = useAuthContext();
  const [userStrategies, setUserStrategies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [refresh,setRefresh]= useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const authToken = getToken();
      const response = await fetch(`${API}/app-user-strategies?filters[user][documentId][$eq]=${user.documentId}&populate=*`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = await response.json();
      setUserStrategies(data.data || []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const authToken = getToken();
      await fetch(`${API}/app-user-strategies/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${authToken}` },
      });
      await fetchData();
      message.success("Strategy deleted successfully!");
    } catch (error) {
      message.error("Failed to delete strategy!");
    }
  };

  const handleUpdate = async (id, payload) => {
    try {
      const authToken = getToken();
      await fetch(`${API}/app-user-strategies/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
        body: JSON.stringify(payload),
      });
      await fetchData();
      message.success("Strategy updated successfully!");
      setRefresh(true);
    } catch (error) {
      message.error("Failed to update strategy!");
    }
  };

  useEffect(() => {
      fetchData();
  }, []);
  useEffect(() => {
    fetchData();
}, [openModal]);


  if (loading) return <Spin size="large" />;

  return (
    <Container>
      <Typography variant="h5" align="center">User Strategies</Typography>
      {userStrategies.length > 0 ? ( 
        userStrategies.map((strategy) =>(
          <StrategyEditor key={strategy.documentId} strategy={strategy} onDelete={handleDelete} onUpdate={handleUpdate} />
        )
      )
      ) : (
        <Typography align="center">No strategies available.</Typography>
      )}
      <Fab color="primary" aria-label="add" onClick={() => setOpenModal(true)}>
        <AddIcon />
      </Fab>
      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <Paper sx={{ p: 2, maxWidth: 500, margin: "auto", mt: 5 }}>
          <UserStrategyForm onClose={() => setOpenModal(false)} indicesList={indicesList} />
        </Paper>
      </Modal>
    </Container>
  );
};

export default Strategies;

